import { Button, Icon } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { useMealPlanGeneration } from '@web-app/hooks/useMealPlanGeneration';
import { useMolecule } from 'bunshi/react';
import { useSetAtom } from 'jotai';
import { TbRefresh } from 'react-icons/tb';

import RegenerateDetailedMealPlanModal from './RegenerateDetailedMealPlanModal';
import RegenerateMealPlanInstructionsModal from './RegenerateMealPlanInstructionsModal';
import RegenerateMealPlanSummaryModal from './RegenerateMealPlanSummaryModal';
import { MealPlanView } from './utils';

type RegenerateStep =
  | MealPlanView.Instructions
  | MealPlanView.Summary
  | MealPlanView.Details;

const BUTTON_LABELS: Record<RegenerateStep, string> = {
  [MealPlanView.Instructions]: 'Regenerate preferences',
  [MealPlanView.Summary]: 'Generate new outline',
  [MealPlanView.Details]: 'Generate new recipes',
};

const BACK_STEPS: Record<
  MealPlanView.Summary | MealPlanView.Details,
  MealPlanView
> = {
  [MealPlanView.Summary]: MealPlanView.Instructions,
  [MealPlanView.Details]: MealPlanView.Summary,
};

interface RegenerateButtonProps {
  step: RegenerateStep;
}

export const RegenerateButton = ({ step }: RegenerateButtonProps) => {
  const { generateInstructions, generateSummary, generateDetailedPlan } =
    useMealPlanGeneration({ forceInstructions: true });
  const { activeMealPlanStepAtom } = useMolecule(mealPlanMolecule);
  const setActiveMealPlanStep = useSetAtom(activeMealPlanStepAtom);

  const label = BUTTON_LABELS[step];

  const trigger = (
    <Button
      variant="ghost"
      size="sm"
      leftIcon={<Icon as={TbRefresh} boxSize={4} />}
      fontWeight={400}
    >
      {label}
    </Button>
  );

  if (step === MealPlanView.Instructions) {
    return (
      <RegenerateMealPlanInstructionsModal onOk={generateInstructions}>
        {trigger}
      </RegenerateMealPlanInstructionsModal>
    );
  }

  const handleRegenerate =
    step === MealPlanView.Summary ? generateSummary : generateDetailedPlan;
  const handleBack = () => setActiveMealPlanStep(BACK_STEPS[step]);

  if (step === MealPlanView.Summary) {
    return (
      <RegenerateMealPlanSummaryModal
        onOk={handleRegenerate}
        onBack={handleBack}
      >
        {trigger}
      </RegenerateMealPlanSummaryModal>
    );
  }

  return (
    <RegenerateDetailedMealPlanModal
      onOk={handleRegenerate}
      onBack={handleBack}
    >
      {trigger}
    </RegenerateDetailedMealPlanModal>
  );
};
